import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Email as EmailIcon,
  Reply as ReplyIcon,
  Forward as ForwardIcon,
  AttachFile as AttachFileIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  AutoAwesome as AIIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import type { EmailDetailsDialogProps } from './types';
import { formatKategorie, cleanText } from './utils';

const EmailDetailsDialog: React.FC<EmailDetailsDialogProps> = ({
  open,
  email,
  attachments,
  onClose,
  onReply,
  onForward,
  onCreateTicket,
  onCreateSummary,
  onAiReply,
  onExtractAppointments,
  onDownloadAttachment,
  ticketAnalyzing = false,
  summarizing = false,
  aiReplySuggesting = false,
  appointmentExtracting = false,
}) => {
  if (!email) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EmailIcon />
          {email.betreffText}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box mb={2}>
          <Typography variant="subtitle2" color="textSecondary">
            {email.istGesendet ? 'An:' : 'Von:'}
          </Typography>
          <Typography>
            {email.istGesendet
              ? email.empfaengerEmail || 'Unbekannt'
              : email.absenderAnzeige}
          </Typography>
        </Box>
        <Box mb={2}>
          <Typography variant="subtitle2" color="textSecondary">
            {email.istGesendet ? 'Gesendet:' : 'Empfangen:'}
          </Typography>
          <Typography>
            {new Date(email.empfangenAm).toLocaleString('de-DE')}
          </Typography>
        </Box>
        {attachments.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" color="textSecondary" mb={1}>
              Anhänge:
            </Typography>
            {attachments.map((attachment) => (
              <Chip
                key={attachment.anhangID}
                icon={<AttachFileIcon />}
                label={`${attachment.dateiName} (${attachment.groesseFormatiert})`}
                onClick={() => onDownloadAttachment(attachment.anhangID, attachment.dateiName)}
                clickable
                color="primary"
                variant="outlined"
                sx={{ mr: 1, mb: 1, cursor: 'pointer' }}
              />
            ))}
          </Box>
        )}
        {email.analysiert && email.kategorie && (
          <Box mb={2}>
            <Typography variant="subtitle2" color="textSecondary" mb={1}>
              KI-Analyse:
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                bgcolor: 'background.default',
                borderLeft: 4,
                borderLeftColor:
                  email.kategorie === 'Wichtig' ? 'error.main' :
                  email.kategorie === 'ZurKenntnis' ? 'info.main' : 'grey.400',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Chip
                  label={formatKategorie(email.kategorie)}
                  color={
                    email.kategorie === 'Wichtig' ? 'error' :
                    email.kategorie === 'ZurKenntnis' ? 'info' : 'default'
                  }
                  size="small"
                />
                {email.analysiertAm && (
                  <Typography variant="caption" color="textSecondary">
                    Analysiert: {new Date(email.analysiertAm).toLocaleString('de-DE')}
                  </Typography>
                )}
              </Box>
              {email.zusammenfassung && (
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{
                    lineHeight: 1.7,
                    fontSize: '0.95rem',
                  }}
                >
                  {cleanText(email.zusammenfassung)}
                </Typography>
              )}
            </Paper>
          </Box>
        )}
        <Box>
          <Typography variant="subtitle2" color="textSecondary" mb={1}>
            Nachricht:
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', minHeight: '300px', maxHeight: '60vh', overflow: 'auto' }}>
            {email.htmlInhalt ? (
              <div dangerouslySetInnerHTML={{ __html: email.htmlInhalt }} />
            ) : email.textInhalt ? (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {email.textInhalt}
              </Typography>
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                Kein E-Mail-Inhalt verfügbar
              </Typography>
            )}
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, p: 2 }}>
        <Button onClick={onClose}>Schließen</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={ticketAnalyzing ? <CircularProgress size={20} /> : <AssignmentIcon />}
            onClick={() => onCreateTicket(email)}
            disabled={ticketAnalyzing}
          >
            {ticketAnalyzing ? 'Analysiere...' : 'Ticket erstellen'}
          </Button>
          <Button
            variant="outlined"
            color="info"
            startIcon={summarizing ? <CircularProgress size={20} /> : <DescriptionIcon />}
            onClick={() => onCreateSummary(email)}
            disabled={summarizing}
          >
            {summarizing ? 'Erstelle...' : 'KI-Zusammenfassung'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={aiReplySuggesting ? <CircularProgress size={20} /> : <AIIcon />}
            onClick={() => onAiReply(email)}
            disabled={aiReplySuggesting}
          >
            {aiReplySuggesting ? 'Generiere...' : 'KI-Antwort'}
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={appointmentExtracting ? <CircularProgress size={20} /> : <CalendarIcon />}
            onClick={() => onExtractAppointments(email)}
            disabled={appointmentExtracting}
          >
            {appointmentExtracting ? 'Extrahiere...' : 'Termine finden'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ForwardIcon />}
            onClick={() => {
              onClose();
              onForward(email);
            }}
          >
            Weiterleiten
          </Button>
          <Button
            variant="contained"
            startIcon={<ReplyIcon />}
            onClick={() => {
              onClose();
              onReply(email);
            }}
          >
            Antworten
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EmailDetailsDialog;
